import { ref, watch, nextTick, onMounted, onUnmounted, unref } from 'vue'

// Positioning and dismissal for a menu that hangs off an anchor element and is
// teleported to body. The host owns the open state (`isOpen`) — this only says
// where the menu goes and when the user asked to leave it.

const VIEWPORT_MARGIN = 10

/** Accept a ref, a getter, or a bare element. */
function resolve(target) {
  return typeof target === 'function' ? target() : unref(target)
}

/**
 * @param {Object} options
 * @param {*} options.anchorRef element the menu hangs off (ref/getter/element)
 * @param {*} options.menuRef the menu element itself (ref/getter/element)
 * @param {*} options.isOpen ref/getter — the host owns open state
 * @param {'left'|'below'|'above'} [options.placement] side the menu prefers
 * @param {'start'|'end'} [options.align] which edges line up on the cross axis:
 *   for 'left', 'start' = top edges and 'end' = bottom edges; for 'below'/'above',
 *   'start' = left edges and 'end' = right edges
 * @param {?number} [options.width] fixed width; null measures the menu
 * @param {number} [options.gap] px between anchor and menu
 * @param {?Function} [options.onDismiss] outside click / Escape
 * @returns {{menuStyle: import('vue').Ref<Object>, reposition: Function}}
 */
export function useAnchoredMenu({
  anchorRef,
  menuRef,
  isOpen,
  placement = 'left',
  align = 'start',
  width = null,
  gap = 8,
  onDismiss = null
}) {
  const menuStyle = ref({})

  const opened = () => !!resolve(isOpen)

  function reposition() {
    const anchor = resolve(anchorRef)
    if (!anchor || !opened()) return

    const a = anchor.getBoundingClientRect()
    const menu = resolve(menuRef)
    const rect = menu ? menu.getBoundingClientRect() : null
    const w = width || rect?.width || 0
    // Height is 0 until the menu has rendered once; the isOpen watcher
    // repositions after nextTick, so the flip decisions get a real height then.
    const h = rect?.height || 0

    let top
    let left

    if (placement === 'left') {
      left = a.left - w - gap
      top = align === 'end' ? a.bottom - h : a.top
    } else {
      left = align === 'end' ? a.right - w : a.left
      if (placement === 'above') {
        top = a.top - h - gap
        // Flip below when there's no room above.
        if (h && top < VIEWPORT_MARGIN) top = a.bottom + gap
      } else {
        top = a.bottom + gap
        // Flip above when there's no room below — but only if above actually fits.
        if (h && top + h > window.innerHeight - VIEWPORT_MARGIN) {
          const flipped = a.top - h - gap
          if (flipped >= VIEWPORT_MARGIN) top = flipped
        }
      }
    }

    // Clamp to the viewport on both axes.
    if (w && left + w > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - w - VIEWPORT_MARGIN
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
    if (h && top + h > window.innerHeight - VIEWPORT_MARGIN) {
      top = window.innerHeight - h - VIEWPORT_MARGIN
    }
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN

    menuStyle.value = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      ...(width ? { minWidth: `${width}px` } : {})
    }
  }

  function handleClickOutside(event) {
    if (!opened() || !onDismiss) return
    const anchor = resolve(anchorRef)
    const menu = resolve(menuRef)
    if (anchor && anchor.contains(event.target)) return
    if (menu && menu.contains(event.target)) return
    onDismiss()
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && opened() && onDismiss) onDismiss()
  }

  // Immediate: hosts may mount a menu that is already open, which would
  // otherwise render unpositioned until something else moved it.
  watch(() => opened(), async (open) => {
    if (!open) return
    reposition()
    // Re-run once the menu exists, so height-dependent flips use a real height.
    await nextTick()
    reposition()
  }, { immediate: true })

  onMounted(() => {
    document.addEventListener('click', handleClickOutside, true)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true)
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', reposition)
    window.removeEventListener('scroll', reposition, true)
  })

  return { menuStyle, reposition }
}
